import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Divider,
  Button,
  Breadcrumbs,
  Link,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowBack as ArrowBackIcon, SupportAgent as SupportIcon } from '@mui/icons-material';

const openHelp = () => document.dispatchEvent(new Event('krillou:open-help'));

const SECTIONS = ['object', 'access', 'obligations', 'transactions', 'deposits', 'cancellation', 'conditionReports', 'disputes', 'penalties', 'ip', 'liability', 'termination', 'changes', 'law', 'contact'];

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const TermsPage = () => {
  const { t } = useTranslation('legal');
  const theme = useTheme();
  const titleRef = useRef(null);
  const location = useLocation();
  const [commissionRate, setCommissionRate] = useState('...');

  useEffect(() => {
    window.scrollTo(0, 0);
    if (titleRef.current) {
      titleRef.current.focus();
    }
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/config/financial`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data?.commission?.rate_percent != null) {
          setCommissionRate(data.data.commission.rate_percent);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!location.hash) {
      return;
    }

    const element = document.getElementById(location.hash.slice(1));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      {/* Header with Breadcrumb */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: 2,
        px: 3,
        borderRadius: 2,
        background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
        color: 'white',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        mb: 4,
        gap: 1.5
      }}>
        <Breadcrumbs sx={{ 
          mb: 0,
          '& .MuiBreadcrumbs-separator': { color: 'rgba(255, 255, 255, 0.7)' }
        }}>
          <Link
            component={RouterLink}
            to="/"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'white', '&:hover': { opacity: 0.8 } }}
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} />
            {t('common:navigation.home', 'Accueil')}
          </Link>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>{t('terms.title')}</Typography>
        </Breadcrumbs>
        <Typography
          ref={titleRef}
          variant="h4"
          component="h1"
          sx={{ fontWeight: 700, color: 'white' }}
          tabIndex={-1}
        >
          {t('terms.title')}
        </Typography>
      </Box>

      {/* Intro */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 5,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
          borderRadius: 2,
        }}
      >
        <Typography sx={{ lineHeight: 1.8 }}>{t('terms.intro')}</Typography>
      </Paper>

      {/* Sections */}
      {SECTIONS.map((key) => {
        const paragraphs = t(`terms.sections.${key}.paragraphs`, { returnObjects: true, commissionRate });
        return (
          <Box key={key} id={key} component="section" sx={{ mb: 5 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
              {t(`terms.sections.${key}.title`)}
            </Typography>

            {Array.isArray(paragraphs) &&
              paragraphs.map((p, i) => (
                <Typography key={i} sx={{ mb: 2, lineHeight: 1.85, color: 'text.secondary' }}>
                  {p}
                </Typography>
              ))}

            {key === 'contact' && (
              <Button
                variant="contained"
                startIcon={<SupportIcon />}
                onClick={openHelp}
                sx={{ mt: 1, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                {t('contactBtn')}
              </Button>
            )}

            <Divider sx={{ mt: 3 }} />
          </Box>
        );
      })}
    </Container>
  );
};

export default TermsPage;
